import { isFunction, isEmpty, debounce } from 'underscore';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useNeedsAttentionForAdapter } from '@bingads-webui-campaign-react/attention-beacon';

export const ValidationBeaconAdapter = (WrappedComponent) => {
  const HOC = ({
    validationProps: validationPropsFromProps,
    attentionBeaconAdapterProps: attentionBeaconAdapterPropsFromProps,
    ...restProps
  }) => {
    const [needsAttentionLocal, setNeedsAttentionLocal] = useState(false);
    const {
      onValidate: onValidateFromProps,
      ...restValidationProps
    } = validationPropsFromProps;
    const [validationState, setValidationState] = useState(() => ({ isDirty: false, messages: [] }));
    const onValidate = useCallback((arg0, ...args) => {
      if (isFunction(onValidateFromProps)) {
        onValidateFromProps(arg0, ...args);
      }
      setValidationState(arg0);
    }, [onValidateFromProps]);

    const setNeedsAttentionLocalWithDebounce = useMemo(() => (
      validationPropsFromProps.debounce ? debounce(setNeedsAttentionLocal, validationPropsFromProps.debounce) : setNeedsAttentionLocal
    ), [validationPropsFromProps.debounce]);

    useEffect(() => {
      // TODO: add onDisplayedErrorsChange to withError instead of reimplementing the error filtering logic
      const { isDirty, messages } = validationState;
      const hasPassiveMessages = !isEmpty(validationPropsFromProps.passiveMessages);
      const hasErrorMessages = isFunction(validationPropsFromProps.errorFilter) ?
        !isEmpty(messages.filter(validationPropsFromProps.errorFilter)) || hasPassiveMessages :
        !isEmpty(messages) || hasPassiveMessages;

      setNeedsAttentionLocalWithDebounce((isDirty || validationPropsFromProps.alwaysShowError || hasPassiveMessages) && hasErrorMessages);
    }, [validationState, validationPropsFromProps.errorFilter, validationPropsFromProps.passiveMessages, validationPropsFromProps.alwaysShowError, setNeedsAttentionLocalWithDebounce]);

    const { attentionBeaconAdapterProps } = useNeedsAttentionForAdapter({ needsAttentionLocal, attentionBeaconAdapterProps: attentionBeaconAdapterPropsFromProps });

    const validationProps = useMemo(() => ({
      ...restValidationProps,
      onValidate,
    }), [restValidationProps, onValidate]);

    return (
      <WrappedComponent
        {...restProps}
        validationProps={validationProps}
        {...WrappedComponent.isAttentionBeaconAdapter ? { attentionBeaconAdapterProps } : {}}
      />
    );
  };

  HOC.propTypes = {
    validationProps: PropTypes.shape({
      onValidate: PropTypes.func,
      alwaysShowError: PropTypes.bool,
      showErrorOnPassiveMessages: PropTypes.bool,
      passiveMessages: PropTypes.arrayOf(PropTypes.string),
      showSingleError: PropTypes.bool,
      errorFilter: PropTypes.func,
      debounce: PropTypes.number,
    }),
    attentionBeaconAdapterProps: PropTypes.shape({
      onAdapterNeedsAttentionChange: PropTypes.func.isRequired,
    }).isRequired,
  };

  HOC.defaultProps = {
    validationProps: {},
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `ValidationBeaconAdapter(${getDisplayName(WrappedComponent)})`;
  HOC.isAttentionBeaconAdapter = true;

  return HOC;
};
